import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuthStore } from '../store/authStore';
import {
  Brain, TrendingUp, Target, Globe, BookOpen, Zap, Award, BarChart3,
  Flame, FileText, Check, X, ChevronDown, Star, Users, Clock, Shield,
  ArrowRight, Play, Sparkles, Calculator as CalcIcon, AlertTriangle,
  CheckCircle2, Layers, Compass, Rocket, Trophy,
} from 'lucide-react';

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user } = useAuthStore();
  const ctaLink = user ? '/exam' : '/login';

  const [calcAnswers, setCalcAnswers] = useState<Record<number, number>>({});
  const [calcResult, setCalcResult] = useState<{
    score: number; passProb: number; confidence: string;
    studyTime: string; strongAreas: string[]; weakAreas: string[]; aiRec: string;
  } | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [yearly, setYearly] = useState(false);
  const [animatedStats, setAnimatedStats] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setAnimatedStats(true),
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const handleCalc = () => {
    if (Object.keys(calcAnswers).length < 4) return;
    const q1 = calcAnswers[1] || 0;
    const q2 = calcAnswers[2] || 0;
    const q3 = calcAnswers[3] || 0;
    const q4 = calcAnswers[4] || 0;
    const score = Math.min(95, 25 + q1 * 15 + q2 * 18 + q3 * 12 + q4 * 10);
    const passProb = Math.min(98, score + 8);
    const confidence = score >= 76 ? t('landing.calcHigh') : score >= 51 ? t('landing.calcModerate') : t('landing.calcLow');
    const weeks = Math.max(1, Math.round((100 - score) / 8));
    const studyTime = weeks >= 4 ? `${Math.round(weeks / 4)} ${t('landing.calcMonths')}` : `${weeks} ${weeks === 1 ? t('landing.calcWeek') : t('landing.calcWeeks')}`;
    const strongAreas = score >= 60 ? [t('landing.calcProcessDomain'), t('landing.calcAgileMindset')] : [t('landing.calcAgileMindset')];
    const weakAreas = score < 80 ? [t('landing.calcRiskMgmt'), t('landing.calcBusinessEnv')] : [t('landing.calcBusinessEnv')];
    const aiRec = isRTL
      ? `أكمل 150 سؤال تدريبي إضافي وحسن أداءك في بيئة الأعمال للوصول إلى 85% جاهزية.`
      : `Complete 150 more practice questions and improve Business Environment performance to reach 85% readiness.`;
    setCalcResult({ score, passProb, confidence, studyTime, strongAreas, weakAreas, aiRec });
  };

  const resetCalc = () => { setCalcAnswers({}); setCalcResult(null); };

  const trustBadges = [
    { icon: Check, label: t('landing.badgeQuestions') },
    { icon: Brain, label: t('landing.badgeAICoach') },
    { icon: Target, label: t('landing.badgeSimulator') },
    { icon: BarChart3, label: t('landing.badgeECO') },
    { icon: Globe, label: t('landing.badgeBilingual') },
    { icon: BookOpen, label: t('landing.badgePMBOK') },
  ];

  const comparisonFeatures = [
    { key: 'featArabic', ours: true, pmi: false, udemy: true },
    { key: 'featAICoach', ours: true, pmi: false, udemy: false },
    { key: 'featReadiness', ours: true, pmi: false, udemy: false },
    { key: 'featWeakness', ours: true, pmi: false, udemy: false },
    { key: 'featECO', ours: true, pmi: false, udemy: false },
    { key: 'featStudyPlan', ours: true, pmi: false, udemy: false },
    { key: 'featAnalytics', ours: true, pmi: true, udemy: false },
    { key: 'featSimulator', ours: true, pmi: true, udemy: false },
    { key: 'featPersonalized', ours: true, pmi: false, udemy: false },
    { key: 'featExplanations', ours: true, pmi: true, udemy: true },
  ];

  const valueProps = [
    { icon: Clock, title: t('landing.vpSaveTime'), desc: t('landing.vpSaveTimeDesc') },
    { icon: Target, title: t('landing.vpWeakAreas'), desc: t('landing.vpWeakAreasDesc') },
    { icon: TrendingUp, title: t('landing.vpTrackReadiness'), desc: t('landing.vpTrackReadinessDesc') },
    { icon: Shield, title: t('landing.vpPracticeReal'), desc: t('landing.vpPracticeRealDesc') },
  ];

  const stats = [
    { value: t('landing.statsQuestions'), label: t('landing.statsQuestionsLabel') },
    { value: t('landing.statsStudents'), label: t('landing.statsStudentsLabel') },
    { value: t('landing.statsPassRate'), label: t('landing.statsPassRateLabel') },
    { value: t('landing.statsCountries'), label: t('landing.statsCountriesLabel') },
  ];

  const trustLogos = ['PMBOK', 'PMI ECO', 'Agile', 'Hybrid', 'Predictive'];

  const featureCards = [
    { icon: Target, title: t('landing.featSimulatorTitle'), desc: t('landing.featSimulatorDesc') },
    { icon: Brain, title: t('landing.featAICoachTitle'), desc: t('landing.featAICoachDesc') },
    { icon: AlertTriangle, title: t('landing.featWeakAreaTitle'), desc: t('landing.featWeakAreaDesc') },
    { icon: BarChart3, title: t('landing.featECOTitle'), desc: t('landing.featECODesc') },
    { icon: Globe, title: t('landing.featBilingualTitle'), desc: t('landing.featBilingualDesc') },
    { icon: BookOpen, title: t('landing.featExplainTitle'), desc: t('landing.featExplainDesc') },
    { icon: Compass, title: t('landing.featStudyPlanTitle'), desc: t('landing.featStudyPlanDesc') },
    { icon: TrendingUp, title: t('landing.featAnalyticsTitle'), desc: t('landing.featAnalyticsDesc') },
    { icon: Flame, title: t('landing.featStreakTitle'), desc: t('landing.featStreakDesc') },
    { icon: FileText, title: t('landing.featMockTitle'), desc: t('landing.featMockDesc') },
  ];

  const showcasePoints = [
    { icon: TrendingUp, text: t('landing.showcaseReadiness') },
    { icon: Award, text: t('landing.showcasePassProb') },
    { icon: Layers, text: t('landing.showcaseKnowledge') },
    { icon: BarChart3, text: t('landing.showcaseProgress') },
    { icon: Brain, text: t('landing.showcaseAI') },
  ];

  const steps = [
    { num: '01', icon: BookOpen, title: t('landing.step1'), desc: t('landing.step1Desc') },
    { num: '02', icon: Target, title: t('landing.step2'), desc: t('landing.step2Desc') },
    { num: '03', icon: TrendingUp, title: t('landing.step3'), desc: t('landing.step3Desc') },
    { num: '04', icon: Trophy, title: t('landing.step4'), desc: t('landing.step4Desc') },
  ];

  const ecoDomains = [
    { icon: Users, name: t('landing.ecoPeople'), desc: t('landing.ecoPeopleDesc'), solved: 342, accuracy: 78, progress: 72, color: 'from-blue-500 to-cyan-500' },
    { icon: Compass, name: t('landing.ecoProcess'), desc: t('landing.ecoProcessDesc'), solved: 528, accuracy: 82, progress: 85, color: 'from-violet-500 to-purple-500' },
    { icon: Rocket, name: t('landing.ecoBusiness'), desc: t('landing.ecoBusinessDesc'), solved: 218, accuracy: 68, progress: 54, color: 'from-amber-500 to-orange-500' },
  ];

  const testimonials = [
    { name: 'Ahmed Hassan', country: isRTL ? 'السعودية' : 'Saudi Arabia', role: isRTL ? 'مدير مشاريع' : 'Project Manager', rating: 5, text: isRTL ? 'اجتزت PMP من أول محاولة بفضل هذه المنصة. التحليلات الذكية ساعدتني كثيراً.' : 'Passed PMP on my first attempt thanks to this platform. The AI analytics were a game-changer.' },
    { name: 'Sarah Mitchell', country: isRTL ? 'كندا' : 'Canada', role: isRTL ? 'مديرة برنامج' : 'Program Director', rating: 5, text: isRTL ? 'محاكي الامتحان واقعي جداً. شعرت وكأنني في الامتحان الحقيقي.' : 'The exam simulator is incredibly realistic. It felt just like the real PMP exam.' },
    { name: 'Khalid Al-Rashid', country: isRTL ? 'الإمارات' : 'UAE', role: isRTL ? 'مدير مشاريع أول' : 'Senior PM', rating: 5, text: isRTL ? 'دعم اللغة العربية ممتاز. شرح كل سؤال مفصل وواضح.' : 'The Arabic support is excellent. Every question has detailed, clear explanations.' },
  ];

  const faqs = [
    { q: t('landing.faq1'), a: t('landing.faq1Ans') },
    { q: t('landing.faq2'), a: t('landing.faq2Ans') },
    { q: t('landing.faq3'), a: t('landing.faq3Ans') },
    { q: t('landing.faq4'), a: t('landing.faq4Ans') },
    { q: t('landing.faq5'), a: t('landing.faq5Ans') },
    { q: t('landing.faq6'), a: t('landing.faq6Ans') },
  ];

  const calcQuestions = [
    { id: 1, q: t('landing.calcQ1'), options: [t('landing.calcQ1A1'), t('landing.calcQ1A2'), t('landing.calcQ1A3'), t('landing.calcQ1A4')] },
    { id: 2, q: t('landing.calcQ2'), options: [t('landing.calcQ2A1'), t('landing.calcQ2A2'), t('landing.calcQ2A3'), t('landing.calcQ2A4')] },
    { id: 3, q: t('landing.calcQ3'), options: [t('landing.calcQ3A1'), t('landing.calcQ3A2'), t('landing.calcQ3A3')] },
    { id: 4, q: t('landing.calcQ4'), options: [t('landing.calcQ4A1'), t('landing.calcQ4A2'), t('landing.calcQ4A3'), t('landing.calcQ4A4')] },
  ];

  const pricingPlans = [
    { name: t('landing.planFree'), price: t('landing.planFreePrice'), period: t('landing.planFreePeriod'), features: [t('landing.featLimited20')], cta: t('landing.getStarted'), popular: false },
    { name: t('landing.planBasic'), price: yearly ? '$7.99' : t('landing.planBasicPrice'), period: t('landing.planBasicPeriod'), features: [t('landing.featFullAccess'), t('landing.featAnalyticsPro')], cta: t('landing.choosePlan'), popular: false },
    { name: t('landing.planPro'), price: yearly ? '$15.99' : t('landing.planProPrice'), period: t('landing.planProPeriod'), features: [t('landing.featUnlimited'), t('landing.featAllFeatures'), t('landing.featAICoachPro'), t('landing.featECOTracking')], cta: t('landing.choosePlan'), popular: true },
    { name: t('landing.planEnterprise'), price: t('landing.planEnterprisePrice'), period: t('landing.planEnterprisePeriod'), features: [t('landing.feat1YearAccess'), t('landing.featTeamLicenses'), t('landing.featCustomBranding'), t('landing.featDedicatedManager')], cta: t('landing.choosePlan'), popular: false },
  ];

  const readinessColor = calcResult
    ? calcResult.score >= 76 ? 'text-emerald-500' : calcResult.score >= 51 ? 'text-amber-500' : 'text-red-500'
    : 'text-primary';
  const readinessStroke = calcResult
    ? calcResult.score >= 76 ? '#10b981' : calcResult.score >= 51 ? '#f59e0b' : '#ef4444'
    : 'hsl(var(--primary))';

  return (
    <>
      <SEO title={t('app.title')} description={t('app.tagline')} canonical="https://pmpqandanswer.web.app/" />
      <div className="overflow-hidden">
        {/* ═══════════════ SECTION 1: HERO ═══════════════ */}
        <section className="relative bg-mesh-gradient pt-12 pb-20 px-4">
          <div className="absolute inset-0 bg-grid-pattern opacity-30" />
          <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-start">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-6 animate-fade-in">
                <Sparkles className="w-4 h-4" />
                {t('landing.badgeECO')}: {t('landing.badgePMBOK')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 text-gradient-primary">
                {t('landing.heroHeadline')}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                {t('landing.heroSub')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-8">
                <Link to={ctaLink} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-bold text-base hover:scale-[1.02] transition-all shadow-lg hover:shadow-glow">
                  <Zap className="w-5 h-5" />
                  {t('landing.startFreeTrial')}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
                <button className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-border bg-card/80 backdrop-blur-sm font-semibold text-base hover:bg-accent transition-all hover:scale-[1.02]">
                  <Play className="w-5 h-5" />
                  {t('landing.watchDemo')}
                </button>
              </div>
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                {trustBadges.map((b, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg glass-card text-xs font-medium border border-border/50 animate-fade-in" style={{ animationDelay: `${i * 0.08}s` }}>
                    <b.icon className="w-3.5 h-3.5 text-emerald-500" />
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
            {/* Right: Dashboard Preview */}
            <div className="relative">
              <div className="glass-card rounded-2xl border border-border/50 shadow-premium-lg p-6 animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-bold">{t('app.title')}</span>
                  <span className="flex items-center gap-1 text-xs text-emerald-500 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Live
                  </span>
                </div>
                {/* Readiness Score Circle */}
                <div className="flex items-center gap-6 mb-5">
                  <div className="relative w-24 h-24 flex-shrink-0">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--primary))" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${74 * 2.64} 264`} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-extrabold text-primary">74%</span>
                      <span className="text-[9px] text-muted-foreground">{t('landing.previewReadiness')}</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('landing.previewAccuracy')}</span>
                      <span className="font-bold">82%</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" style={{ width: '82%' }} />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('landing.previewStreak')}</span>
                      <span className="font-bold flex items-center gap-1"><Flame className="w-3 h-3 text-orange-500" />12</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('landing.previewSolved')}</span>
                      <span className="font-bold">1,088</span>
                    </div>
                  </div>
                </div>
                {/* AI Recommendation */}
                <div className="rounded-xl bg-primary/5 border border-primary/20 p-3">
                  <div className="flex items-start gap-2">
                    <Brain className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-primary mb-0.5">{t('landing.previewAI')}</p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{t('landing.previewAIMsg')}</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating badges */}
              <div className="absolute -top-3 -right-3 glass-card rounded-xl border border-border/50 px-3 py-2 shadow-lg animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <div className="flex items-center gap-1.5">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-bold">92% {t('landing.statsPassRateLabel')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION X: READINESS CALCULATOR ═══════════════ */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/5 via-purple-500/5 to-transparent">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
                <CalcIcon className="w-4 h-4" />
                {t('landing.calcTitle')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.calcTitle')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t('landing.calcSubtitle')}</p>
            </div>
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Questions Panel */}
              <div className="glass-card rounded-2xl border border-border/50 shadow-premium-lg p-6 md:p-8">
                <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary text-sm font-bold">1</span>
                  {t('landing.calcQuestions')}
                </h3>
                <div className="space-y-6">
                  {calcQuestions.map((q) => (
                    <div key={q.id}>
                      <p className="text-sm font-semibold mb-3">{q.q}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {q.options.map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => setCalcAnswers({ ...calcAnswers, [q.id]: i })}
                            className={`px-3 py-2.5 rounded-xl text-xs font-medium border transition-all hover:scale-[1.02] ${
                              calcAnswers[q.id] === i
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border bg-card/50 hover:bg-accent'
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCalc}
                    disabled={Object.keys(calcAnswers).length < 4}
                    className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-bold text-sm hover:scale-[1.02] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
                  >
                    {t('landing.calcCalculate')}
                  </button>
                  <button onClick={resetCalc} className="px-5 py-3 rounded-xl border border-border font-semibold text-sm hover:bg-accent transition-all">
                    {t('landing.calcReset')}
                  </button>
                </div>
                {Object.keys(calcAnswers).length < 4 && (
                  <p className="text-xs text-muted-foreground mt-3 text-center">{t('landing.calcAnswerAll')}</p>
                )}
              </div>
              {/* Results Panel */}
              <div className="glass-card rounded-2xl border border-border/50 shadow-premium-lg p-6 md:p-8">
                <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary text-sm font-bold">2</span>
                  {t('landing.calcResults')}
                </h3>
                {calcResult ? (
                  <div className="animate-fade-in">
                    {/* Circular Gauge */}
                    <div className="flex flex-col items-center mb-6">
                      <div className="relative w-40 h-40">
                        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                          <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
                          <circle cx="50" cy="50" r="42" fill="none" stroke={readinessStroke} strokeWidth="6" strokeLinecap="round" strokeDasharray={`${calcResult.score * 2.64} 264`} className="transition-all duration-1000" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className={`text-4xl font-extrabold ${readinessColor}`}>{calcResult.score}%</span>
                          <span className="text-xs text-muted-foreground">{t('landing.calcReady')}</span>
                        </div>
                      </div>
                    </div>
                    {/* Metrics */}
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div className="rounded-xl bg-card/50 border border-border p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">{t('landing.calcPassProb')}</p>
                        <p className="text-xl font-bold text-emerald-500">{calcResult.passProb}%</p>
                      </div>
                      <div className="rounded-xl bg-card/50 border border-border p-3 text-center">
                        <p className="text-xs text-muted-foreground mb-1">{t('landing.calcConfidence')}</p>
                        <p className="text-xl font-bold">{calcResult.confidence}</p>
                      </div>
                      <div className="rounded-xl bg-card/50 border border-border p-3 text-center col-span-2">
                        <p className="text-xs text-muted-foreground mb-1">{t('landing.calcStudyTime')}</p>
                        <p className="text-lg font-bold">{calcResult.studyTime}</p>
                      </div>
                    </div>
                    {/* Strong / Weak Areas */}
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-3">
                        <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 mb-2">{t('landing.calcStrongAreas')}</p>
                        {calcResult.strongAreas.map((a, i) => (
                          <p key={i} className="text-xs flex items-center gap-1.5 mb-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {a}</p>
                        ))}
                      </div>
                      <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3">
                        <p className="text-xs font-bold text-amber-600 dark:text-amber-400 mb-2">{t('landing.calcWeakAreas')}</p>
                        {calcResult.weakAreas.map((a, i) => (
                          <p key={i} className="text-xs flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> {a}</p>
                        ))}
                      </div>
                    </div>
                    {/* AI Rec */}
                    <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 mb-5">
                      <div className="flex items-start gap-2">
                        <Brain className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-primary mb-0.5">{t('landing.calcAIRec')}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{calcResult.aiRec}</p>
                        </div>
                      </div>
                    </div>
                    {/* CTA */}
                    <div className="text-center">
                      <p className="text-sm font-bold mb-3">{t('landing.calcCTATitle')}</p>
                      <p className="text-xs text-muted-foreground mb-4">{t('landing.calcCTASub')}</p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Link to={ctaLink} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-bold text-sm hover:scale-[1.02] transition-all shadow-md text-center">
                          {t('landing.calcCTABtn')}
                        </Link>
                        <Link to="/exam" className="flex-1 px-4 py-2.5 rounded-xl border border-border font-semibold text-sm hover:bg-accent transition-all text-center">
                          {t('landing.calcCTASecondary')}
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[400px] text-center">
                    <div className="relative w-32 h-32 mb-4">
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <CalcIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{t('landing.calcSubtitle')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 2: WHY US ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.whyTitle')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t('landing.whySubtitle')}</p>
            </div>
            {/* Comparison Table */}
            <div className="glass-card rounded-2xl border border-border/50 shadow-premium-lg overflow-hidden mb-12">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-start p-4 text-sm font-bold">{t('landing.colFeature')}</th>
                      <th className="p-4 text-sm font-bold text-primary bg-primary/5">{t('landing.colOurPlatform')}</th>
                      <th className="p-4 text-sm font-bold text-muted-foreground">{t('landing.colPMI')}</th>
                      <th className="p-4 text-sm font-bold text-muted-foreground">{t('landing.colUdemy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((f, i) => (
                      <tr key={i} className="border-b border-border/50 last:border-0 hover:bg-accent/30 transition-colors">
                        <td className="p-4 text-sm font-medium">{t(`landing.${f.key}`)}</td>
                        <td className="p-4 text-center bg-primary/5">
                          {f.ours ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <X className="w-5 h-5 text-muted-foreground/30 mx-auto" />}
                        </td>
                        <td className="p-4 text-center">
                          {f.pmi ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <X className="w-5 h-5 text-muted-foreground/30 mx-auto" />}
                        </td>
                        <td className="p-4 text-center">
                          {f.udemy ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /> : <X className="w-5 h-5 text-muted-foreground/30 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Value Proposition Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {valueProps.map((vp, i) => (
                <div key={i} className="glass-card rounded-2xl border border-border/50 p-6 hover:shadow-premium-lg transition-all duration-200 hover:scale-[1.02]">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center mb-4">
                    <vp.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-base mb-2">{vp.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{vp.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 3: SOCIAL PROOF ═══════════════ */}
        <section ref={statsRef} className="py-20 px-4 bg-gradient-to-br from-primary/5 to-purple-500/5">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {stats.map((s, i) => (
                <div key={i} className="text-center" style={{ opacity: animatedStats ? 1 : 0, transform: animatedStats ? 'scale(1)' : 'scale(0.8)', transition: `all 0.5s ease ${i * 0.1}s` }}>
                  <div className="text-4xl md:text-5xl font-extrabold text-gradient-primary mb-2">{s.value}</div>
                  <div className="text-sm text-muted-foreground font-medium">{s.label}</div>
                </div>
              ))}
            </div>
            {/* Trust Logos */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-4 uppercase tracking-wider">{t('landing.trustLogos')}</p>
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                {trustLogos.map((logo, i) => (
                  <span key={i} className="px-4 py-2 rounded-xl glass-card border border-border/50 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors">
                    {logo}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 4: FEATURES ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.featuresTitle')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t('landing.featuresSubtitle')}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
              {featureCards.map((f, i) => (
                <div key={i} className="group glass-card rounded-2xl border border-border/50 p-5 hover:shadow-premium-lg transition-all duration-200 hover:scale-[1.03] cursor-default">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary/15 to-purple-500/15 flex items-center justify-center mb-4 group-hover:from-primary/25 group-hover:to-purple-500/25 transition-all">
                    <f.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-bold text-sm mb-2 leading-snug">{f.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 5: DASHBOARD SHOWCASE ═══════════════ */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/5 to-transparent">
          <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.showcaseTitle')}</h2>
              <p className="text-muted-foreground mb-8 max-w-lg">{t('landing.showcaseSubtitle')}</p>
              <div className="space-y-4">
                {showcasePoints.map((p, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-all">
                      <p.icon className="w-5 h-5 text-primary" />
                    </div>
                    <p className="text-sm font-medium pt-2">{p.text}</p>
                  </div>
                ))}
              </div>
            </div>
            {/* Mockup */}
            <div className="relative">
              <div className="glass-card rounded-2xl border border-border/50 shadow-premium-lg p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl bg-gradient-to-br from-primary/10 to-purple-500/10 p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-extrabold text-primary">74%</p>
                    <p className="text-[10px] text-muted-foreground">{t('landing.previewReadiness')}</p>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 p-4 text-center">
                    <Award className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                    <p className="text-2xl font-extrabold text-emerald-500">82%</p>
                    <p className="text-[10px] text-muted-foreground">{t('landing.calcPassProb')}</p>
                  </div>
                  <div className="rounded-xl bg-card/50 border border-border p-4 col-span-2">
                    <p className="text-xs font-bold mb-3">{t('landing.showcaseKnowledge')}</p>
                    <div className="space-y-2">
                      {['People', 'Process', 'Business'].map((d, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-[10px] w-20 text-muted-foreground">{d}</span>
                          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-primary to-purple-500" style={{ width: `${[78, 85, 54][i]}%` }} />
                          </div>
                          <span className="text-[10px] font-bold w-8 text-end">{[78, 85, 54][i]}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-xl bg-card/50 border border-border p-4 col-span-2">
                    <div className="flex items-start gap-2">
                      <Brain className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-muted-foreground leading-relaxed">{t('landing.previewAIMsg')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 6: HOW IT WORKS ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.howTitle')}</h2>
              <p className="text-muted-foreground">{t('landing.howSubtitle')}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
              {steps.map((s, i) => (
                <div key={i} className="relative">
                  {i < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-border to-transparent" />
                  )}
                  <div className="glass-card rounded-2xl border border-border/50 p-6 text-center hover:shadow-premium-lg transition-all hover:scale-[1.02]">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary to-purple-600 opacity-10" />
                      <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-primary/15 to-purple-500/15 flex items-center justify-center">
                        <s.icon className="w-7 h-7 text-primary" />
                      </div>
                      <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">{s.num}</span>
                    </div>
                    <h3 className="font-bold text-sm mb-2">{s.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 7: ECO DOMAIN TRACKING ═══════════════ */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/5 to-purple-500/5">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.ecoTitle')}</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">{t('landing.ecoSubtitle')}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {ecoDomains.map((d, i) => (
                <div key={i} className="glass-card rounded-2xl border border-border/50 p-6 hover:shadow-premium-lg transition-all hover:scale-[1.02]">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${d.color} flex items-center justify-center mb-4 shadow-md`}>
                    <d.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{d.name}</h3>
                  <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{d.desc}</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('landing.ecoQuestionsSolved')}</span>
                      <span className="font-bold">{d.solved}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{t('landing.ecoAccuracy')}</span>
                      <span className="font-bold">{d.accuracy}%</span>
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-muted-foreground">{t('landing.ecoProgress')}</span>
                        <span className="font-bold">{d.progress}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${d.color}`} style={{ width: `${d.progress}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 8: TESTIMONIALS ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.testimonialsTitle')}</h2>
              <p className="text-muted-foreground">{t('landing.testimonialsSubtitle')}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((tm, i) => (
                <div key={i} className="glass-card rounded-2xl border border-border/50 p-6 hover:shadow-premium-lg transition-all hover:scale-[1.02]">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: tm.rating }).map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5 italic">"{tm.text}"</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-border/50">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                      {tm.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold truncate">{tm.name}</p>
                      <p className="text-xs text-muted-foreground">{tm.role} • {tm.country}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-500/15 text-emerald-600 text-[10px] font-bold">
                      <Trophy className="w-3 h-3" /> PMP
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 9: PRICING ═══════════════ */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/5 to-purple-500/5">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.pricingTitle')}</h2>
              <p className="text-muted-foreground mb-6">{t('landing.pricingSubtitle')}</p>
              {/* Monthly/Yearly Toggle */}
              <div className="inline-flex items-center gap-3 p-1 rounded-xl border border-border bg-card/50">
                <button onClick={() => setYearly(false)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${!yearly ? 'bg-primary text-white' : 'text-muted-foreground'}`}>
                  {t('landing.monthly')}
                </button>
                <button onClick={() => setYearly(true)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${yearly ? 'bg-primary text-white' : 'text-muted-foreground'}`}>
                  {t('landing.yearly')} <span className="text-xs text-emerald-400">{t('landing.saveBadge')}</span>
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {pricingPlans.map((plan, i) => (
                <div key={i} className={`relative rounded-2xl border p-6 transition-all hover:scale-[1.02] ${
                  plan.popular
                    ? 'border-primary bg-gradient-to-b from-primary/10 to-transparent shadow-premium-lg lg:scale-105'
                    : 'border-border/50 glass-card hover:shadow-premium-lg'
                }`}>
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-purple-600 text-white text-xs font-bold whitespace-nowrap shadow-md">
                      ⭐ {t('landing.mostPopular')}
                    </span>
                  )}
                  <h3 className="font-bold text-lg mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-extrabold">{plan.price}</span>
                    <span className="text-xs text-muted-foreground">/ {plan.period}</span>
                  </div>
                  {plan.name === t('landing.planFree') && <p className="text-xs text-muted-foreground mb-4">{t('landing.freeTrial10')}</p>}
                  <ul className="space-y-2.5 mb-6 mt-4">
                    {plan.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={ctaLink}
                    className={`block w-full text-center px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-primary to-purple-600 text-white hover:scale-[1.02] shadow-md'
                        : 'border border-border hover:bg-accent'
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 10: FAQ ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-3">{t('landing.faqTitle')}</h2>
              <p className="text-muted-foreground">{t('landing.faqSubtitle')}</p>
            </div>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <div key={i} className="glass-card rounded-xl border border-border/50 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-start hover:bg-accent/30 transition-colors"
                  >
                    <span className="text-sm font-semibold">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed animate-fade-in">
                      {f.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════ SECTION 11: FINAL CTA ═══════════════ */}
        <section className="py-20 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="relative rounded-3xl bg-gradient-to-br from-primary via-purple-600 to-purple-700 p-12 md:p-16 text-center overflow-hidden">
              <div className="absolute inset-0 bg-grid-pattern opacity-10" />
              <div className="relative">
                <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">{t('landing.finalTitle')}</h2>
                <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t('landing.finalSub')}</p>
                <Link to={ctaLink} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white text-primary font-bold text-lg hover:scale-[1.02] transition-all shadow-xl">
                  <Zap className="w-5 h-5" />
                  {t('landing.finalCTA')}
                  <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════ FOOTER ═══════════════ */}
        <footer className="border-t border-border bg-card/50 py-12 px-4">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white font-bold text-sm">P</div>
                  <span className="font-bold text-sm">PMP Exam Simulator</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{t('landing.footerDesc')}</p>
              </div>
              <div>
                <p className="text-sm font-bold mb-3">{t('landing.footerAbout')}</p>
                <ul className="space-y-2">
                  <li><Link to="/blog" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('landing.footerBlog')}</Link></li>
                  <li><Link to="/pricing" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('landing.footerPricing')}</Link></li>
                  <li><Link to="/contact" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('landing.footerContact')}</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm font-bold mb-3">{t('landing.footerTerms')}</p>
                <ul className="space-y-2">
                  <li><Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('landing.footerTerms')}</Link></li>
                  <li><Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">{t('landing.footerPrivacy')}</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-sm font-bold mb-3">{t('landing.footerContact')}</p>
                <div className="flex gap-2">
                  {['𝕏', 'in', 'f'].map((s, i) => (
                    <a key={i} href="#" className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-sm font-bold text-muted-foreground hover:bg-accent hover:text-foreground transition-all">{s}</a>
                  ))}
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} PMP Exam Simulator. {t('landing.footerRights')}</p>
              <button
                onClick={() => { const nl = i18n.language === 'en' ? 'ar' : 'en'; i18n.changeLanguage(nl); localStorage.setItem('lang', nl); }}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border hover:bg-accent transition-all"
              >
                {isRTL ? '🇬🇧 English' : '🇸🇦 العربية'}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
